import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { endSignupAnalytics } from '@Shared/utils/posthog';

const { Title, Paragraph } = Typography;

// How long the page stays: until the page has gone quiet — the marketing pixels on /complete/ and the funnel's last
// events have left — not a fixed time (Vova 30.10.2026). Never under three seconds, so the page is read rather than
// flashed; a slow connection waits for its requests, and nobody waits past the cap; the one-time link into the account
// lives two minutes.
const MIN_STAY_MS = 3000;
const QUIET_MS = 1000; // no request finished for this long
const MAX_STAY_MS = 8000;

// Resolves once no request has finished for QUIET_MS (after MIN_STAY_MS), or at MAX_STAY_MS. A request is seen when it
// finishes (the browser's resource timing), so one still in flight past the quiet second is not waited for: pixels and
// event calls are short, and the cap bounds the rest.
function waitForQuiet(onQuiet: () => void): () => void {
    const start = performance.now();
    let lastActivity = start;
    const observer =
        typeof PerformanceObserver === 'undefined'
            ? null
            : new PerformanceObserver(() => {
                  lastActivity = performance.now();
              });
    try {
        observer?.observe({ type: 'resource', buffered: false });
    } catch {
        // an old browser without resource timing: the minimum and the cap still hold
    }
    const timer = setInterval(() => {
        const now = performance.now();
        const quiet = now - start >= MIN_STAY_MS && now - lastActivity >= QUIET_MS;
        if (quiet || now - start >= MAX_STAY_MS) {
            clearInterval(timer);
            observer?.disconnect();
            onQuiet();
        }
    }, 200);
    return () => {
        clearInterval(timer);
        observer?.disconnect();
    };
}

// After the trial step: the account exists, the browser goes into it by the one-time link the server gave.
// Opened without one (a reload, a typed address) it sends the visitor to the login page.
export default function CompleteStep() {
    const { t } = useTranslation('translation', { keyPrefix: 'signup.complete_step' });
    const redirectLink = (useLocation().state as { redirectLink?: string } | null)?.redirectLink;

    useEffect(() => {
        if (!redirectLink) {
            endSignupAnalytics();
            window.location.replace('https://app.tapfiliate.com/login/');
            return undefined;
        }
        return waitForQuiet(() => {
            endSignupAnalytics();
            window.location.replace(redirectLink);
        });
    }, [redirectLink]);

    return (
        <div style={{ width: 'min(calc(100vw - 40px), 500px)', padding: 40, textAlign: 'center' }}>
            <Spin size="large" />
            <Title
                level={4}
                style={{ margin: '24px 0 8px' }}
            >
                {t('title')}
            </Title>
            <Paragraph
                type="secondary"
                style={{ margin: 0 }}
            >
                {t('text')}
            </Paragraph>
        </div>
    );
}
